import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { IconButton, Snackbar, SnackbarContent, Stack } from '@mui/material';
import { tr } from '../lib/i18n';
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { ApiError } from '../api/client';

type Kind = 'success' | 'error' | 'info' | 'warning';
interface ToastApi {
  toast: (message: string, kind?: Kind) => void;
  toastError: (err: unknown) => void;
}
const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<{ text: string; kind: Kind; key: number } | null>(null);
  const toast = useCallback((text: string, kind: Kind = 'success') => setMsg({ text, kind, key: Date.now() }), []);
  const toastError = useCallback((err: unknown) => {
    if (err instanceof ApiError) {
      const detail = err.details.find((d) => d.message && d.message !== err.message)?.message;
      toast(detail ? `${err.message} — ${detail}` : err.message, 'error');
    } else toast(err instanceof Error ? err.message : tr('حدث خطأ', 'An error occurred'), 'error');
  }, [toast]);
  return (
    <Ctx.Provider value={{ toast, toastError }}>
      {children}
      <Snackbar
        key={msg?.key}
        open={!!msg}
        autoHideDuration={msg?.kind === 'error' ? 6000 : 4000}
        onClose={(_, reason) => reason !== 'clickaway' && setMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarContent
          message={
            <Stack direction="row" spacing={1.25} alignItems="center">
              {msg?.kind === 'error' ? <ErrorOutlineIcon sx={{ color: '#F2B8B5', fontSize: 20 }} />
                : msg?.kind === 'warning' ? <WarningAmberIcon sx={{ color: '#FFB95C', fontSize: 20 }} />
                : <CheckCircleOutlineIcon sx={{ color: '#A8DAB5', fontSize: 20 }} />}
              <span>{msg?.text}</span>
            </Stack>
          }
          action={
            <IconButton size="small" aria-label={tr('إغلاق', 'Close')} onClick={() => setMsg(null)} sx={{ color: 'inherit' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{ minWidth: { sm: 344 }, maxWidth: 560, fontSize: '0.875rem' }}
        />
      </Snackbar>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v;
}
